import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ShieldCheck, MapPin, Heart, ListChecks, FlaskConical, Footprints, MessageCircle, ExternalLink } from "lucide-react";
import { Reveal, MaskLine, ease } from "../components/Motion";
import { SampleLesson } from "../components/SampleLesson";
import { ProofBand } from "../components/ProofBand";
import { FaqCallout } from "../components/FaqCallout";
import { IMAGES, VERIFY } from "../data/content";
import { HOME } from "../data/home";
import { FAQ, HOME_FAQ_IDS } from "../data/faq";
import { SEO } from "../data/seo";
import { useSEO } from "../lib/seo";

const HOME_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQ_IDS.map((id) => FAQ.find((f) => f.id === id)).map((f) => ({
    "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const BUILT_ICONS = [ListChecks, FlaskConical, Footprints, MessageCircle];
const scrollToMethod = () => document.getElementById("method")?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function Home() {
  useSEO({ ...SEO.home, jsonLd: HOME_FAQ_LD });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-28 pb-20">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
          <img
            src="/img/hero-1264.jpg"
            srcSet="/img/hero-640.jpg 640w, /img/hero-960.jpg 960w, /img/hero-1264.jpg 1264w"
            sizes="100vw"
            width="1264"
            height="848"
            fetchPriority="high"
            alt="A veteran and his family sitting close together outdoors at golden hour"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,27,42,0.92) 0%, rgba(13,27,42,0.5) 45%, rgba(13,27,42,0.12) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(13,27,42,0.55) 0%, rgba(13,27,42,0) 60%)" }} />
        </motion.div>

        <div className="max-w-7xl mx-auto w-full px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
            className="label text-gold mb-6 flex items-center gap-3 drop-shadow-[0_1px_8px_rgba(10,27,36,0.8)]"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-gold/60" /> {HOME.eyebrow}
          </motion.p>

          <h1 className="font-serif text-cream text-[10.5vw] leading-[0.95] sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tight drop-shadow-[0_2px_20px_rgba(10,27,36,0.4)]" data-testid="hero-headline">
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7, ease }}
              className="block mb-5 font-sans not-italic font-medium tracking-normal leading-snug text-cream/90 text-lg sm:text-xl md:text-2xl max-w-2xl"
              data-testid="hero-category"
            >
              {HOME.category}
            </motion.span>
            <MaskLine delay={0.45}>{HOME.headline[0]}</MaskLine>
            <MaskLine delay={0.55}>{HOME.headline[1]}</MaskLine>
            <MaskLine delay={0.65} className="italic text-gold">{HOME.headline[2]}</MaskLine>
          </h1>

          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }}
              className="lg:col-span-6 max-w-xl"
            >
              <p className="text-base md:text-lg text-cream/80 leading-relaxed" data-testid="hero-intro">{HOME.intro}</p>
              <p className="mt-4 text-sm md:text-base text-cream/70 leading-relaxed" data-testid="hero-pain">{HOME.pain}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease }}
              className="lg:col-span-6 lg:justify-self-end flex flex-col items-start lg:items-end gap-3"
            >
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/learn/intro" data-testid="hero-cta" className="group inline-flex items-center gap-3 rounded-full bg-gold text-navy-deep pl-7 pr-3 py-3 text-base font-semibold hover:bg-cream transition-colors duration-300">
                  {HOME.ctaPrimary}
                  <span className="w-9 h-9 rounded-full bg-navy text-cream flex items-center justify-center group-hover:translate-x-0.5 transition-transform duration-300">
                    <ArrowUpRight size={18} />
                  </span>
                </Link>
                <button onClick={scrollToMethod} data-testid="hero-method" className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream px-6 py-3 text-base font-semibold hover:bg-cream/10 transition-colors duration-300">
                  {HOME.ctaSecondary} <ArrowRight size={16} className="rotate-90" />
                </button>
              </div>
              <p className="text-sm text-cream/70 flex flex-wrap items-center gap-x-3" data-testid="hero-cta-note">
                <span>{HOME.ctaPrimaryNote}</span>
                <Link to="/professor-mentale#curriculum" data-testid="hero-curriculum" className="underline underline-offset-4 decoration-cream/40 hover:text-cream hover:decoration-cream transition-colors">
                  {HOME.ctaTertiary}
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 bg-navy text-cream border-y border-navy-deep/20">
        <Marquee speed={38} gradient={false} autoFill>
          {["Better Questions", "Deeper Understanding", "Stronger Conversations", "Behavioral-Health Education", "Veteran-Owned", "The Investigation Method™"].map((t, i) => (
            <span key={i} className="font-serif italic text-2xl md:text-3xl mx-8 flex items-center gap-8">
              {t} <span className="text-gold not-italic">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* A DIFFERENT WAY */}
      <section className="py-24 md:py-36 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-different">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl text-navy-deep leading-tight">{HOME.different.title}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <p className="text-lg md:text-xl text-navy-deep/80 leading-relaxed">{HOME.different.p1}</p>
              <p className="mt-6 text-lg text-navy-deep/70 leading-relaxed">{HOME.different.p2}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SEE ONE LESSON IN ACTION */}
      <SampleLesson />

      {/* THE INVESTIGATION METHOD */}
      <section id="method" className="scroll-mt-24 pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-method">
        <div className="rounded-[2rem] bg-navy text-cream p-8 md:p-14">
          <Reveal>
            <p className="label text-gold mb-4">{HOME.method.eyebrow}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl">{HOME.method.title}</h2>
          </Reveal>
          <ol className="mt-10 flex flex-wrap gap-2.5" data-testid="home-method-stages">
            {HOME.method.stages.map((s, i) => (
              <Reveal as="li" key={s} delay={i * 0.04} className="inline-flex items-center gap-2.5 rounded-full bg-cream/5 border border-cream/15 pl-3 pr-4 py-2" data-testid={`home-stage-${s.toLowerCase()}`}>
                <span className="font-serif text-sm text-gold/90">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-base md:text-lg uppercase tracking-wide">{s}</span>
                {i < HOME.method.stages.length - 1 && <ArrowRight size={14} className="text-gold/60" aria-hidden="true" />}
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.1}>
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-cream/80 max-w-2xl">{HOME.method.caption}</p>
            <Link to="/professor-mentale" data-testid="home-method-course" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:gap-3 transition-all">
              See how the 20 lessons walk these stages <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* BUILT FOR REAL-LIFE REFLECTION */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-built">
        <Reveal><h2 className="font-serif text-3xl md:text-5xl text-navy-deep leading-tight mb-10">{HOME.built.title}</h2></Reveal>
        <div className="grid md:grid-cols-2 gap-4">
          {HOME.built.items.map((t, i) => {
            const Icon = BUILT_ICONS[i];
            return (
              <Reveal key={t} delay={i * 0.07}>
                <div className="h-full rounded-2xl bg-white border border-navy/10 p-7 flex gap-5" data-testid={`home-built-${i}`}>
                  <span className="w-12 h-12 rounded-xl bg-sage/25 text-navy flex items-center justify-center shrink-0"><Icon size={22} strokeWidth={1.6} /></span>
                  <p className="text-lg text-navy-deep/85 leading-relaxed">{t}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHAT CHANGES */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-changes">
        <div className="rounded-[2rem] bg-navy text-cream p-8 md:p-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="label text-gold mb-4">{HOME.changes.eyebrow}</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">{HOME.changes.title}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {HOME.changes.items.map((c, i) => (
              <Reveal key={c.k} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-cream/5 border border-cream/10 p-6" data-testid={`home-changes-${i}`}>
                  <p className="label text-cream/50">{c.k}</p>
                  <p className="mt-3 text-cream/85 leading-relaxed">{c.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="py-24 md:py-32 bg-white/60 border-y border-navy/10" data-testid="home-who">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl text-navy-deep leading-tight">{HOME.who.title}</h2>
            <p className="mt-5 mb-10 text-lg md:text-xl text-navy-deep/75 leading-relaxed max-w-2xl" data-testid="home-who-lead">{HOME.who.lead}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-4">
            {HOME.who.audiences.map((a, i) => (
              <Reveal key={a.k} delay={i * 0.08}>
                <Link to={a.to} data-testid={`home-who-${i}`} className={`group block h-full rounded-2xl border p-8 transition-transform hover:-translate-y-0.5 ${i === 2 ? "bg-navy border-navy text-cream" : "bg-white border-navy/10 text-navy-deep"}`}>
                  <p className="font-serif text-2xl md:text-3xl leading-tight">{a.k}</p>
                  <p className={`mt-4 leading-relaxed ${i === 2 ? "text-cream/75" : "text-navy-deep/70"}`}>{a.s}</p>
                  <span className={`mt-6 inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all ${i === 2 ? "text-gold" : "text-navy"}`}>{a.cta} <ArrowUpRight size={14} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm md:text-base text-navy-deep/65 leading-relaxed max-w-3xl border-l-2 border-gold/60 pl-5" data-testid="home-who-notfor">{HOME.who.notFor}</p>
          </Reveal>
        </div>
      </section>

      {/* INSTITUTIONS */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-institutions">
        <Reveal>
          <div className="rounded-2xl bg-white border border-navy/10 p-7 md:p-9 grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <p className="label text-sage mb-3">{HOME.institutions.eyebrow}</p>
              <p className="font-serif text-2xl md:text-3xl text-navy-deep leading-snug">{HOME.institutions.title}</p>
              <p className="mt-3 text-navy-deep/65 leading-relaxed">{HOME.institutions.body}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <Link to="/services" data-testid="home-institutions-services" className="inline-flex items-center justify-between gap-3 rounded-full border border-navy/20 text-navy px-6 py-3 font-semibold hover:bg-navy hover:text-cream transition-colors">
                Programs and services <ArrowUpRight size={16} />
              </Link>
              <Link to="/government-contracting" data-testid="home-institutions-government" className="inline-flex items-center justify-between gap-3 rounded-full border border-navy/20 text-navy px-6 py-3 font-semibold hover:bg-navy hover:text-cream transition-colors">
                Government contracting <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PROOF */}
      <ProofBand />

      {/* PRICING BAND */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-pricing">
        <Reveal>
          <div className="mb-8">
            <p className="label text-sage mb-3">Pricing</p>
            <h2 className="font-serif text-3xl md:text-5xl text-navy-deep leading-tight">Clear about cost, for every audience.</h2>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-4">
          <Reveal className="lg:col-span-7">
            <div className="h-full rounded-2xl bg-white border border-navy/10 p-7 md:p-9" data-testid="home-pricing-individuals">
              <p className="label text-navy/50">For individuals</p>
              <p className="font-serif text-3xl md:text-4xl text-navy-deep mt-3 leading-tight">Free to $47. One-time, no subscription.</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-navy-deep/75">
                <li><span className="font-semibold text-navy-deep">Free</span> — Introduction + Lesson 1, no account, no card</li>
                <li><span className="font-semibold text-navy-deep">$7</span> — one lesson</li>
                <li><span className="font-semibold text-navy-deep">$19</span> — a module of four lessons</li>
                <li><span className="font-semibold text-navy-deep">$47</span> — all 20 lessons, five module certificates and the course certificate</li>
              </ul>
              <Link to="/pricing" data-testid="home-see-pricing" className="mt-7 inline-flex items-center gap-3 rounded-full bg-navy text-cream px-7 py-3.5 font-semibold hover:bg-navy-light transition-colors">
                See pricing <ArrowUpRight size={18} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="h-full rounded-2xl bg-sage/20 border border-sage/30 p-7 md:p-9" data-testid="home-pricing-institutions">
              <p className="label text-navy/50">For schools, agencies and teaming partners</p>
              <p className="font-serif text-2xl md:text-3xl text-navy-deep mt-3 leading-tight">Scoped per engagement; government work priced to the statement of work.</p>
              <p className="mt-3 text-sm text-navy-deep/65 leading-relaxed">Programs are priced to population, cohort size, facilitation and reporting, and come back as a written proposal within two business days.</p>
              <p className="mt-5 text-sm">
                <Link to="/pricing#partners" data-testid="home-pricing-schools" className="font-semibold text-navy underline underline-offset-4 decoration-navy/30 hover:decoration-navy">How partner pricing works</Link>
                <span className="text-navy-deep/40 mx-2">·</span>
                <Link to="/pricing#government" data-testid="home-pricing-government" className="font-semibold text-navy underline underline-offset-4 decoration-navy/30 hover:decoration-navy">Government</Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ CALLOUT */}
      <FaqCallout />

      {/* START WITH ONE QUESTION */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-start">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-navy px-8 py-16 md:p-20">
            <img src={IMAGES.emblem} alt="" className="absolute -right-16 -bottom-16 w-72 md:w-96 opacity-20 rotate-6" />
            <div className="relative max-w-3xl">
              <p className="label text-gold mb-5">{HOME.start.title}</p>
              <h2 className="font-serif italic text-3xl md:text-5xl lg:text-6xl text-cream leading-tight" data-testid="home-start-question">
                &ldquo;{HOME.start.question}&rdquo;
              </h2>
              <p className="mt-6 text-lg md:text-xl text-cream/80">{HOME.start.line}</p>
              <Link to="/learn/l1" data-testid="home-start-cta" className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold text-navy-deep px-8 py-4 font-semibold hover:bg-cream transition-colors duration-300">
                {HOME.start.cta} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm text-navy-deep/60 max-w-3xl" data-testid="home-disclaimer">{HOME.disclaimer}</p>
        </Reveal>
      </section>

      {/* TRUST RIBBON */}
      <section className="py-20 bg-sage/20 border-y border-navy/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10">
          {[
            { icon: ShieldCheck, t: "Veteran-Owned Small Business", s: "VOSB — SBA Certified · Active in SAM.gov", link: { href: VERIFY.dsbs, label: "Verify on SBA DSBS" } },
            { icon: MapPin, t: "Texas-Based", s: "Serving Texas communities & beyond" },
            { icon: Heart, t: "From Diploma to PhD in Progress", s: "A disabled veteran who went from a high-school diploma at retirement to a PhD in progress — lived proof of the work", link: { to: "/about", label: "Meet the founder" } },
          ].map((item, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex flex-col gap-4">
                <span className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-navy shadow-[0_8px_30px_rgba(14,84,120,0.06)]">
                  <item.icon size={26} strokeWidth={1.6} />
                </span>
                <h4 className="font-serif text-2xl text-navy-deep">{item.t}</h4>
                <p className="text-navy-deep/65">{item.s}</p>
                {item.link?.href && (
                  <a href={item.link.href} target="_blank" rel="noreferrer" data-testid="ribbon-verify-vosb" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:text-gold transition-colors">{item.link.label} <ExternalLink size={14} /></a>
                )}
                {item.link?.to && (
                  <Link to={item.link.to} data-testid="ribbon-founder-link" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:gap-2 transition-all">{item.link.label} <ArrowUpRight size={14} /></Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
